import React, { useMemo, useState } from 'react';
import { Box, Button } from 'rebass';
import { Label } from '@rebass/forms';
import SenderProfileSelect from 'components/SenderProfileSelect';
import { EmailCcTokenizer } from 'components/EmailCcTokenizer';

/**
 * Determines which sender profile to use and whether to show the profile selector.
 *
 * Selection Logic:
 * 1. If the current user's email matches one of the available sender profiles,
 *    automatically use that profile (userSenderProfile).
 * 2. If no match is found, fall back to the first available sender profile.
 * 3. Only show the sender profile dropdown when:
 *    - There are multiple sender profiles available, AND
 *    - The user doesn't have a matching sender profile
 *    This prevents showing a dropdown when there's only one option,
 *    or when the user's profile should be used automatically.
 *
 * @param {Array} senderProfiles - Available sender profiles with email and id properties.
 * @param {string} userEmail - Current user's email address.
 * @returns {Object} Object containing userSenderProfile, defaultSenderProfile, and showSenderProfileSelect.
 */
export const getSenderProfileSelection = (senderProfiles, userEmail) => {
    const userSenderProfile = senderProfiles.find(senderProfile => senderProfile.email === userEmail);
    const defaultSenderProfile = userSenderProfile || senderProfiles?.[0];
    const showSenderProfileSelect = senderProfiles.length > 1 && !userSenderProfile;

    return { userSenderProfile, defaultSenderProfile, showSenderProfileSelect };
};

/**
 * SendEmailForm is a functional component used to render a form for sending emails.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.onSubmit - A callback function to handle form submission.
 * @param {Array<string>} [props.defaultValue] - An optional array of default CC recipient values.
 * @param {Array} props.senderProfiles - Available sender profiles.
 * @param {string} [props.userEmail] - Current user's email for default selection.
 * @param {boolean} [props.showCcField=true] - Whether to show the CC recipients field.
 * @returns {JSX.Element} The rendered form component.
 */
const SendEmailForm = ({ onSubmit, defaultValue, senderProfiles, userEmail, showCcField = true }) => {
    const [recipientsCc, setRecipientsCc] = useState(defaultValue || []);

    const { defaultSenderProfile, showSenderProfileSelect } = useMemo(
        () => getSenderProfileSelection(senderProfiles, userEmail),
        [senderProfiles, userEmail]
    );

    const [fromEmail, setFromEmail] = useState(defaultSenderProfile?.email);

    const submit = event => {
        event.preventDefault();
        onSubmit({ recipientsCc, fromEmail });
    };

    return (
        <form onSubmit={submit}>
            {showSenderProfileSelect && (
                <Box mb={3}>
                    <Label htmlFor="sender-profile-select">From</Label>
                    <SenderProfileSelect
                        id="sender-profile-select"
                        style={{ maxWidth: '100%' }}
                        senderProfiles={senderProfiles}
                        defaultValue={defaultSenderProfile?.id}
                        onChange={id => {
                            const senderProfile = senderProfiles.find(sp => sp.id === id);
                            setFromEmail(senderProfile.email);
                        }}
                    />
                </Box>
            )}
            {showCcField && (
                <>
                    <Label htmlFor="recipients-cc-input">CC Recipients</Label>
                    <EmailCcTokenizer
                        id="recipients-cc-input"
                        addCcButtonVariant="secondary-gray"
                        addButtonVariant="secondary"
                        className="mb-4"
                        value={recipientsCc}
                        onChange={value => setRecipientsCc(value)}
                    />
                </>
            )}
            <div className="flex justify-end">
                <Button type="submit">Send</Button>
            </div>
        </form>
    );
};

export default SendEmailForm;
